import { useState } from 'react'
import { Button } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Label } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Building2, Plus, X, Wand2, Package, Hammer, Map, Users, Box } from 'lucide-react'
import { useBuildingStore } from '../../../../stores/buildingStore'
import type { BuildingInput, BuildingType, BuildingGenre, BuildingScale, DetailLevel } from '@creanizer/types'

interface BuildingFormProps {
  onSubmit: (input: BuildingInput) => Promise<void>
  isGenerating: boolean
  initialValues?: Partial<BuildingInput>
  savedWorlds?: any[]
  savedCharacters?: any[]
  savedItems?: any[]
  savedCommunities?: any[]
  savedSocieties?: any[]
  useStreaming?: boolean
  onStreamingChange?: (value: boolean) => void
}

export function BuildingForm({ 
  onSubmit, 
  isGenerating, 
  initialValues,
  savedWorlds = [],
  savedCharacters = [],
  savedItems = [],
  savedCommunities = [],
  savedSocieties = [],
  useStreaming,
  onStreamingChange
}: BuildingFormProps) {
  const { buildings } = useBuildingStore()
  const [mode, setMode] = useState<BuildingInput['mode']>(initialValues?.mode || 'standard')
  const [formData, setFormData] = useState<BuildingInput>({
    name: initialValues?.name || '',
    buildingType: initialValues?.buildingType || 'residential',
    scale: initialValues?.scale || 'house',
    specificScale: initialValues?.specificScale || '',
    genre: initialValues?.genre || 'fantasy',
    era: initialValues?.era || '',
    culturalStyle: initialValues?.culturalStyle || '',
    theme: initialValues?.theme || '',
    purpose: initialValues?.purpose || '',
    currentState: initialValues?.currentState || '',
    ownerType: initialValues?.ownerType || '',
    location: initialValues?.location || {},
    mode: initialValues?.mode || 'standard',
    ideaInput: initialValues?.ideaInput || '',
    renovationBase: initialValues?.renovationBase || undefined,
    numberOfBuildings: initialValues?.numberOfBuildings || 3,
    batchAdditionalInfo: initialValues?.batchAdditionalInfo || '',
    specialFeatures: initialValues?.specialFeatures || [],
    detailLevel: initialValues?.detailLevel || 'standard',
    creativityLevel: initialValues?.creativityLevel || 7,
    useProMode: initialValues?.useProMode || false,
    useProLiteMode: initialValues?.useProLiteMode || false,
    worldContext: initialValues?.worldContext || '',
    relatedCharacterContext: initialValues?.relatedCharacterContext || '',
    characterRelationship: initialValues?.characterRelationship || '',
    relatedItemContext: initialValues?.relatedItemContext || '',
    itemPlacement: initialValues?.itemPlacement || '',
    relatedBuildingContext: initialValues?.relatedBuildingContext || '',
    buildingRelationship: initialValues?.buildingRelationship || '',
    relatedCommunityContext: initialValues?.relatedCommunityContext || '',
    communityRelationship: initialValues?.communityRelationship || '',
    relatedSocietyContext: initialValues?.relatedSocietyContext || '',
    societyRelationship: initialValues?.societyRelationship || '',
    additionalSettings: initialValues?.additionalSettings || '',
    language: initialValues?.language || 'ja'
  })
  
  const [newFeature, setNewFeature] = useState('')

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // アイデアモードの場合、不要なデフォルト値を除外
    if (formData.mode === 'idea') {
      const ideaModeData: BuildingInput = {
        mode: 'idea',
        ideaInput: formData.ideaInput,
        language: formData.language,
        useProMode: formData.useProMode,
        useProLiteMode: formData.useProLiteMode,
        creativityLevel: formData.creativityLevel,
        // 世界観コンテキストは維持（アイデアモードでも使用可能）
        worldContext: formData.worldContext,
        relatedCharacterContext: formData.relatedCharacterContext,
        characterRelationship: formData.characterRelationship,
        relatedItemContext: formData.relatedItemContext,
        itemPlacement: formData.itemPlacement,
        relatedBuildingContext: formData.relatedBuildingContext,
        buildingRelationship: formData.buildingRelationship,
        relatedCommunityContext: formData.relatedCommunityContext,
        communityRelationship: formData.communityRelationship,
        relatedSocietyContext: formData.relatedSocietyContext,
        societyRelationship: formData.societyRelationship,
        additionalSettings: formData.additionalSettings
      }
      await onSubmit(ideaModeData)
    } else {
      await onSubmit(formData)
    }
  }

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        specialFeatures: [...(prev.specialFeatures || []), newFeature.trim()]
      }))
      setNewFeature('')
    }
  }

  const removeFeature = (index: number) => {
    setFormData(prev => ({
      ...prev,
      specialFeatures: prev.specialFeatures?.filter((_, i) => i !== index) || []
    }))
  }


  return (
    <form onSubmit={handleSubmit} className="space-y-6">
        {/* モード選択 */}
        <div className="grid grid-cols-4 gap-2">
          <Button
            type="button"
            variant={mode === 'standard' ? 'default' : 'outline'}
            onClick={() => {
              setMode('standard')
              setFormData(prev => ({ ...prev, mode: 'standard' }))
            }}
            className="flex items-center gap-2"
          >
            <Building2 className="h-4 w-4" />
            標準
          </Button>
          <Button
            type="button"
            variant={mode === 'idea' ? 'default' : 'outline'}
            onClick={() => {
              setMode('idea')
              setFormData(prev => ({ ...prev, mode: 'idea' }))
            }}
            className="flex items-center gap-2"
          >
            <Wand2 className="h-4 w-4" />
            アイデア
          </Button>
          <Button
            type="button"
            variant={mode === 'batch' ? 'default' : 'outline'}
            onClick={() => {
              setMode('batch')
              setFormData(prev => ({ ...prev, mode: 'batch' }))
            }}
            className="flex items-center gap-2"
          >
            <Package className="h-4 w-4" />
            バッチ
          </Button>
          <Button
            type="button"
            variant={mode === 'renovation' ? 'default' : 'outline'}
            onClick={() => {
              setMode('renovation')
              setFormData(prev => ({ ...prev, mode: 'renovation' }))
            }}
            className="flex items-center gap-2"
          >
            <Hammer className="h-4 w-4" />
            リノベーション
          </Button>
        </div>

        {/* アイデアモード */}
        {mode === 'idea' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="ideaInput">アイデア入力</Label>
              <Textarea
                id="ideaInput"
                value={formData.ideaInput}
                onChange={(e) => setFormData(prev => ({ ...prev, ideaInput: e.target.value }))}
                placeholder="例: 空に浮かぶ図書館、生きている家、時間が歪む塔、水中都市の中央神殿..."
                className="mt-1"
                rows={4}
                required
              />
              <p className="text-sm text-muted-foreground mt-1">
                自由な発想で建造物のアイデアを入力してください
              </p>
            </div>
          </div>
        )}

        {/* バッチモード */}
        {mode === 'batch' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="numberOfBuildings">生成数</Label>
              <Select
                value={formData.numberOfBuildings?.toString()}
                onValueChange={(value) => setFormData(prev => ({ ...prev, numberOfBuildings: parseInt(value) }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5, 6, 7].map(num => (
                    <SelectItem key={num} value={num.toString()}>
                      {num}個
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="batchAdditionalInfo">追加情報</Label>
              <Textarea
                id="batchAdditionalInfo"
                value={formData.batchAdditionalInfo || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, batchAdditionalInfo: e.target.value }))}
                placeholder="バッチ生成に関する追加の指示や情報を入力..."
                className="mt-1"
                rows={3}
              />
            </div>
          </div>
        )}

        {/* リノベーションモード */}
        {mode === 'renovation' && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="renovationBase">リノベーション元建造物</Label>
              <Select
                value={formData.renovationBase?.basicInfo?.name || ''}
                onValueChange={(value) => {
                  const building = buildings.find(b => b.result.basicInfo.name === value)
                  if (building) {
                    setFormData(prev => ({ ...prev, renovationBase: building.result }))
                  }
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="建造物を選択..." />
                </SelectTrigger>
                <SelectContent>
                  {buildings.map(building => (
                    <SelectItem key={building.id} value={building.result.basicInfo.name}>
                      {building.result.basicInfo.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-sm text-muted-foreground mt-1">
                既存の建造物を選択して改築・改装します
              </p>
            </div>
          </div>
        )}

        {/* 標準・バッチ・リノベーション共通フィールド */}
        {(mode === 'standard' || mode === 'batch' || mode === 'renovation') && (
          <>
            {/* 基本情報 */}
            <div className="space-y-4">
              <h3 className="font-medium">基本情報</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">建造物名</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="例: 蒼天の塔、影の館..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="buildingType">建造物タイプ</Label>
                  <Select
                    value={formData.buildingType}
                    onValueChange={(value: BuildingType) => setFormData(prev => ({ ...prev, buildingType: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="residential">住居</SelectItem>
                      <SelectItem value="commercial">商業</SelectItem>
                      <SelectItem value="industrial">工業</SelectItem>
                      <SelectItem value="religious">宗教</SelectItem>
                      <SelectItem value="military">軍事</SelectItem>
                      <SelectItem value="educational">教育</SelectItem>
                      <SelectItem value="medical">医療</SelectItem>
                      <SelectItem value="entertainment">娯楽</SelectItem>
                      <SelectItem value="governmental">政府</SelectItem>
                      <SelectItem value="transportation">交通</SelectItem>
                      <SelectItem value="mixed_use">複合用途</SelectItem>
                      <SelectItem value="custom">カスタム</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="scale">規模</Label>
                  <Select
                    value={formData.scale}
                    onValueChange={(value: BuildingScale) => setFormData(prev => ({ ...prev, scale: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="oneroom">ワンルーム規模（1部屋のみ）</SelectItem>
                      <SelectItem value="apartment">マンションの一室規模（数部屋）</SelectItem>
                      <SelectItem value="house">一軒家規模</SelectItem>
                      <SelectItem value="mansion">屋敷規模</SelectItem>
                      <SelectItem value="building">ビル規模</SelectItem>
                      <SelectItem value="skyscraper">高層ビル規模</SelectItem>
                      <SelectItem value="megastructure">超巨大建造物</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="specificScale">具体的な規模</Label>
                  <Input
                    id="specificScale"
                    value={formData.specificScale}
                    onChange={(e) => setFormData(prev => ({ ...prev, specificScale: e.target.value }))}
                    placeholder="例: 10階建て、500㎡..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* ジャンル・世界観 */}
            <div className="space-y-4">
              <h3 className="font-medium">ジャンル・世界観</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="genre">ジャンル</Label>
                  <Select
                    value={formData.genre}
                    onValueChange={(value: BuildingGenre) => setFormData(prev => ({ ...prev, genre: value }))}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fantasy">ファンタジー</SelectItem>
                      <SelectItem value="medieval">中世</SelectItem>
                      <SelectItem value="modern">現代</SelectItem>
                      <SelectItem value="futuristic">未来</SelectItem>
                      <SelectItem value="steampunk">スチームパンク</SelectItem>
                      <SelectItem value="cyberpunk">サイバーパンク</SelectItem>
                      <SelectItem value="horror">ホラー</SelectItem>
                      <SelectItem value="ancient">古代</SelectItem>
                      <SelectItem value="alien">異星</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="era">時代</Label>
                  <Input
                    id="era"
                    value={formData.era}
                    onChange={(e) => setFormData(prev => ({ ...prev, era: e.target.value }))}
                    placeholder="例: 中世、近未来、古代..."
                    className="mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="culturalStyle">文化様式</Label>
                  <Input
                    id="culturalStyle"
                    value={formData.culturalStyle}
                    onChange={(e) => setFormData(prev => ({ ...prev, culturalStyle: e.target.value }))}
                    placeholder="例: ゴシック、和風、アールデコ..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="theme">テーマ</Label>
                  <Input
                    id="theme"
                    value={formData.theme}
                    onChange={(e) => setFormData(prev => ({ ...prev, theme: e.target.value }))}
                    placeholder="例: 幻想的、荘厳、不気味..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>

            {/* 用途・状態 */}
            <div className="space-y-4">
              <h3 className="font-medium">用途・状態</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="purpose">用途・目的</Label>
                  <Input
                    id="purpose"
                    value={formData.purpose}
                    onChange={(e) => setFormData(prev => ({ ...prev, purpose: e.target.value }))}
                    placeholder="例: 居住、防衛、宗教、商業..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="currentState">現在の状態</Label>
                  <Input
                    id="currentState"
                    value={formData.currentState}
                    onChange={(e) => setFormData(prev => ({ ...prev, currentState: e.target.value }))}
                    placeholder="例: 活動中、廃墟、建設中..."
                    className="mt-1"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="ownerType">所有者タイプ</Label>
                <Input
                  id="ownerType"
                  value={formData.ownerType}
                  onChange={(e) => setFormData(prev => ({ ...prev, ownerType: e.target.value }))}
                  placeholder="例: 貴族、商人、政府、宗教団体..."
                  className="mt-1"
                />
              </div>
            </div>

            {/* 場所設定 */}
            <div className="space-y-4">
              <h3 className="font-medium">場所設定</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="terrain">地形</Label>
                  <Input
                    id="terrain"
                    value={formData.location?.terrain || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, terrain: e.target.value }
                    }))}
                    placeholder="例: 山頂、海岸、森林..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="climate">気候</Label>
                  <Input
                    id="climate"
                    value={formData.location?.climate || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, climate: e.target.value }
                    }))}
                    placeholder="例: 温帯、寒冷、熱帯..."
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="neighborhood">周辺環境</Label>
                  <Input
                    id="neighborhood"
                    value={formData.location?.neighborhood || ''}
                    onChange={(e) => setFormData(prev => ({ 
                      ...prev, 
                      location: { ...prev.location, neighborhood: e.target.value }
                    }))}
                    placeholder="例: 商業地区、住宅街..."
                    className="mt-1"
                  />
                </div>
              </div>
            </div>
          </>
        )}

        {/* 詳細設定（全モード共通） */}
        <div className="space-y-4">
          <h3 className="font-medium">詳細設定</h3>
          
          {/* 特殊要素 */}
          <div>
            <Label>特殊要素</Label>
            <div className="flex gap-2 mt-1">
              <Input
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="特殊要素を追加..."
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    addFeature()
                  }
                }}
              />
              <Button type="button" onClick={addFeature} size="icon">
                <Plus className="h-4 w-4" />
              </Button>
            </div>
            {formData.specialFeatures && formData.specialFeatures.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-2">
                {formData.specialFeatures.map((feature, index) => (
                  <Badge key={index} variant="secondary" className="flex items-center gap-1">
                    {feature}
                    <button
                      type="button"
                      onClick={() => removeFeature(index)}
                      className="ml-1 hover:text-destructive"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>


          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="detailLevel">詳細度</Label>
              <Select
                value={formData.detailLevel}
                onValueChange={(value: DetailLevel) => setFormData(prev => ({ ...prev, detailLevel: value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="basic">基本</SelectItem>
                  <SelectItem value="standard">標準</SelectItem>
                  <SelectItem value="detailed">詳細</SelectItem>
                  <SelectItem value="exhaustive">完全</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="creativityLevel">創造性レベル ({formData.creativityLevel})</Label>
              <Slider
                id="creativityLevel"
                value={[formData.creativityLevel || 7]}
                onValueChange={([value]) => setFormData(prev => ({ ...prev, creativityLevel: value }))}
                min={1}
                max={10}
                step={1}
                className="mt-1"
              />
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useProMode"
              checked={formData.useProMode}
              onCheckedChange={(checked) => setFormData(prev => ({ 
                ...prev, 
                useProMode: checked as boolean,
                useProLiteMode: checked ? false : prev.useProLiteMode // Proモードがオンの時はProライトをオフ
              }))}
            />
            <Label htmlFor="useProMode">Proモード（より詳細な生成）</Label>
          </div>
          
          <div className="flex items-center space-x-2">
            <Checkbox
              id="useProLiteMode"
              checked={formData.useProLiteMode}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                useProLiteMode: checked as boolean,
                useProMode: checked ? false : prev.useProMode // Proライトがオンの時はProモードをオフ
              }))}
            />
            <Label htmlFor="useProLiteMode" className="flex items-center space-x-2">
              <span>Proモードライト（実験的）</span>
              <span className="text-xs text-muted-foreground">[thinking_budget: 1024]</span>
            </Label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="useGemini3FlashMode"
              checked={formData.useGemini3FlashMode}
              onCheckedChange={(checked) => setFormData(prev => ({
                ...prev,
                useGemini3FlashMode: checked as boolean
              }))}
            />
            <Label htmlFor="useGemini3FlashMode">Gemini 3 Flash モード（試験版）</Label>
          </div>

          {onStreamingChange && (
            <div className="flex items-center space-x-2">
              <Checkbox
                id="useStreaming"
                checked={useStreaming ?? true}
                onCheckedChange={(checked) => onStreamingChange(checked === true)}
              />
              <Label htmlFor="useStreaming" className="cursor-pointer">
                ストリーミング生成（リアルタイム表示）
              </Label>
            </div>
          )}
        </div>

        {/* 関連データ */}
        <div className="space-y-4">
          <h3 className="font-medium">関連データ</h3>
          
          {/* 世界観連携 */}
          <div>
            <Label htmlFor="worldContext">世界観連携</Label>
            <Select
              value={formData.worldContext || 'none'}
              onValueChange={(value) => setFormData(prev => ({ ...prev, worldContext: value === 'none' ? '' : value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="世界観を選択..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">なし</SelectItem>
                {savedWorlds.map(world => (
                  <SelectItem key={world.id} value={world.id}>
                    {world.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* キャラクター連携 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="relatedCharacterContext">関連キャラクター</Label>
              <Select
                value={formData.relatedCharacterContext || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, relatedCharacterContext: value === 'none' ? '' : value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="キャラクターを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {savedCharacters.map(char => (
                    <SelectItem key={char.id} value={char.id}>
                      {char.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="characterRelationship">関係性</Label>
              <Input
                id="characterRelationship"
                value={formData.characterRelationship}
                onChange={(e) => setFormData(prev => ({ ...prev, characterRelationship: e.target.value }))}
                placeholder="例: 所有者、居住者..."
                className="mt-1"
              />
            </div>
          </div>

          {/* アイテム連携 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="relatedItemContext">関連アイテム</Label>
              <Select
                value={formData.relatedItemContext || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, relatedItemContext: value === 'none' ? '' : value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="アイテムを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {savedItems.map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="itemPlacement">配置場所</Label>
              <Input
                id="itemPlacement"
                value={formData.itemPlacement}
                onChange={(e) => setFormData(prev => ({ ...prev, itemPlacement: e.target.value }))}
                placeholder="例: 宝物庫、玉座の間..."
                className="mt-1"
              />
            </div>
          </div>

          {/* 建造物連携 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="relatedBuildingContext">関連建造物</Label>
              <Select
                value={formData.relatedBuildingContext || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, relatedBuildingContext: value === 'none' ? '' : value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="建造物を選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {buildings.map(building => (
                    <SelectItem key={building.id} value={building.id}>
                      {building.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="buildingRelationship">関係性</Label>
              <Input
                id="buildingRelationship"
                value={formData.buildingRelationship}
                onChange={(e) => setFormData(prev => ({ ...prev, buildingRelationship: e.target.value }))}
                placeholder="例: 隣接、地下通路で接続..."
                className="mt-1"
              />
            </div>
          </div>
          
          {/* コミュニティ連携 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="relatedCommunityContext">所在コミュニティ</Label>
              <Select
                value={formData.relatedCommunityContext || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, relatedCommunityContext: value === 'none' ? '' : value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="コミュニティを選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {savedCommunities.map(community => (
                    <SelectItem key={community.id} value={community.id}>
                      {community.result?.basicInfo?.name || community.title} ({community.result?.basicInfo?.type || '不明'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="communityRelationship">関係性</Label>
              <Input
                id="communityRelationship"
                value={formData.communityRelationship}
                onChange={(e) => setFormData(prev => ({ ...prev, communityRelationship: e.target.value }))}
                placeholder="例: 中心部、商業地区、住宅街..."
                className="mt-1"
              />
            </div>
          </div>
          
          {/* ソサエティ連携 */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="relatedSocietyContext">関連組織</Label>
              <Select
                value={formData.relatedSocietyContext || 'none'}
                onValueChange={(value) => setFormData(prev => ({ ...prev, relatedSocietyContext: value === 'none' ? '' : value }))}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="組織を選択..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">なし</SelectItem>
                  {savedSocieties.map(society => (
                    <SelectItem key={society.id} value={society.id}>
                      {society.result?.basicInfo?.name || society.title} ({society.result?.basicInfo?.type || '不明'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="societyRelationship">関係性</Label>
              <Input
                id="societyRelationship"
                value={formData.societyRelationship}
                onChange={(e) => setFormData(prev => ({ ...prev, societyRelationship: e.target.value }))}
                placeholder="例: 本部、支部、所有施設..."
                className="mt-1"
              />
            </div>
          </div>
        </div>

        {/* その他設定 */}
        <div className="space-y-4">
          <div>
            <Label htmlFor="additionalSettings">追加設定</Label>
            <Textarea
              id="additionalSettings"
              value={formData.additionalSettings}
              onChange={(e) => setFormData(prev => ({ ...prev, additionalSettings: e.target.value }))}
              placeholder="その他の詳細設定や要望を入力..."
              className="mt-1"
              rows={3}
            />
          </div>

          <div>
            <Label htmlFor="language">言語</Label>
            <Select
              value={formData.language}
              onValueChange={(value: 'ja' | 'en') => setFormData(prev => ({ ...prev, language: value }))}
            >
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ja">日本語</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <Button 
          type="submit" 
          className="w-full" 
          disabled={isGenerating}
        >
          {isGenerating ? '生成中...' : '建造物を生成'}
        </Button>
    </form>
  )
}